/**
 * OpenQuestionsSection — inquiries on a ball (handoff_inquiries).
 *
 * An inquiry gets an answer without moving the ball (canon 29: clarification
 * is not a Turn — possession stays where it is). Anyone permitted on the loop
 * may ask; anyone permitted other than the asker answers; the asker withdraws.
 * Writes go through the catchball_*_inquiry RPCs (authority: migration 718)
 * and the list is refetched from the server after each — nothing is assumed.
 */

import { useMemo, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { useToast } from '../../../ui/Toast';
import { useActorDisplayMap } from '../../../../hooks/prioritization/useActorDisplayMap';
import { openInquiry } from '../../../../lib/commands/openInquiry';
import { answerInquiry } from '../../../../lib/commands/answerInquiry';
import { cancelInquiry } from '../../../../lib/commands/cancelInquiry';
import {
  INQUIRY_STATUS_LABELS,
  canAnswerInquiry,
  canAskQuestion,
  canWithdrawInquiry,
  countOpen,
  inquiryDisplayStatus,
  sortInquiries,
  type LoopInquiry,
} from './threadLogic';
import {
  GatedButton,
  InlineError,
  PRIMARY_BUTTON_CLASS,
  SECTION_CLASS,
  ThreadComposer,
  formatWhen,
} from './ThreadComposer';

const STATUS_CLASS: Record<string, string> = {
  open: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  answered: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  withdrawn: 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
  other: 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
};

export function OpenQuestionsSection({
  loopId,
  viewerId,
  viewerName,
  inquiries,
  readError,
  onChanged,
}: {
  loopId: string;
  viewerId: string | null;
  viewerName?: string | null;
  inquiries: LoopInquiry[];
  readError: string | null;
  /** Refetch from the server; awaited before a write is reported as done. */
  onChanged: () => Promise<void>;
}) {
  const { showToast } = useToast();
  const [asking, setAsking] = useState(false);
  const [answeringId, setAnsweringId] = useState<string | null>(null);
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);

  const sorted = useMemo(() => sortInquiries(inquiries, viewerId), [inquiries, viewerId]);
  const actorIds = useMemo(
    () => inquiries.flatMap((q) => [q.openedBy, q.answeredBy]),
    [inquiries],
  );
  const names = useActorDisplayMap(actorIds);
  const nameOf = (id: string | null) =>
    !id ? 'Someone' : id === viewerId ? 'You' : names[id] ?? 'A colleague';

  const openCount = countOpen(inquiries);
  const askGate = canAskQuestion(viewerId);

  return (
    <section id="inbox-questions" className={SECTION_CLASS}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            <HelpCircle className="h-4 w-4 text-[var(--text-ter)]" />
            Open questions
            {openCount > 0 && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)]">
                {openCount} open
              </span>
            )}
          </h2>
          <p className="text-xs text-[var(--text-ter)] mt-1">
            A question gets you an answer without moving the ball — possession stays where it is.
          </p>
        </div>
        {!asking && (
          <GatedButton gate={askGate} onClick={() => setAsking(true)} className={PRIMARY_BUTTON_CLASS}>
            Ask a question
          </GatedButton>
        )}
      </div>

      <InlineError message={readError ? `Could not refresh questions: ${readError}` : null} />

      {asking && viewerId && (
        <ThreadComposer
          label="Question"
          placeholder="What do you need to know before you can act on this?"
          submitLabel="Ask"
          busyLabel="Asking…"
          onCancel={() => setAsking(false)}
          onSubmit={async (question) => {
            await openInquiry({ loopId, question, actorId: viewerId, actorName: viewerName ?? null });
            await onChanged();
            setAsking(false);
            showToast('success', 'Question asked', 'Everyone on this ball can see and answer it.');
          }}
        />
      )}

      {sorted.length === 0 ? (
        <p className="text-xs text-[var(--text-ter)]">No questions on this ball yet.</p>
      ) : (
        <ul className="divide-y divide-[var(--rule)] border-t border-[var(--rule)]">
          {sorted.map((q) => {
            const status = inquiryDisplayStatus(q);
            const answerGate = canAnswerInquiry(q, viewerId);
            const withdrawGate = canWithdrawInquiry(q, viewerId);
            const mine = Boolean(viewerId) && q.openedBy === viewerId;
            return (
              <li key={q.id} className="py-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-[var(--text-ter)]">
                    <span className="font-medium text-[var(--text-sec)]">{nameOf(q.openedBy)}</span> asked
                    {formatWhen(q.openedAt) && ` · ${formatWhen(q.openedAt)}`}
                  </p>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[status]}`}>
                    {INQUIRY_STATUS_LABELS[status]}
                  </span>
                </div>
                <p className="text-sm text-[var(--text-pri)] whitespace-pre-wrap">{q.question}</p>

                {q.answer && (
                  <div className="border-l-2 border-[var(--green)] bg-[var(--surface)] px-3 py-2">
                    <p className="text-xs text-[var(--text-ter)]">
                      <span className="font-medium text-[var(--text-sec)]">{nameOf(q.answeredBy)}</span> answered
                      {formatWhen(q.answeredAt) && ` · ${formatWhen(q.answeredAt)}`}
                    </p>
                    <p className="text-sm text-[var(--text-sec)] whitespace-pre-wrap mt-1">{q.answer}</p>
                  </div>
                )}

                {status === 'open' && answeringId !== q.id && withdrawingId !== q.id && (
                  <div className="flex flex-wrap items-start gap-2">
                    {mine ? (
                      <GatedButton gate={withdrawGate} onClick={() => setWithdrawingId(q.id)}>
                        Withdraw
                      </GatedButton>
                    ) : (
                      <GatedButton
                        gate={answerGate}
                        onClick={() => setAnsweringId(q.id)}
                        className={PRIMARY_BUTTON_CLASS}
                      >
                        Answer
                      </GatedButton>
                    )}
                    {mine && (
                      <span className="text-[11px] leading-snug text-[var(--text-ter)] self-center">
                        Waiting on someone else on this ball to answer.
                      </span>
                    )}
                  </div>
                )}

                {answeringId === q.id && viewerId && (
                  <ThreadComposer
                    label="Answer"
                    submitLabel="Send answer"
                    busyLabel="Sending…"
                    onCancel={() => setAnsweringId(null)}
                    onSubmit={async (answer) => {
                      await answerInquiry({ inquiryId: q.id, answer, actorId: viewerId, actorName: viewerName ?? null });
                      await onChanged();
                      setAnsweringId(null);
                      showToast('success', 'Answer sent');
                    }}
                  />
                )}

                {withdrawingId === q.id && viewerId && (
                  <ThreadComposer
                    label="Why withdraw (optional)"
                    required={false}
                    submitLabel="Withdraw question"
                    busyLabel="Withdrawing…"
                    onCancel={() => setWithdrawingId(null)}
                    onSubmit={async (rationale) => {
                      await cancelInquiry({
                        inquiryId: q.id,
                        actorId: viewerId,
                        actorName: viewerName ?? null,
                        rationale: rationale || null,
                      });
                      await onChanged();
                      setWithdrawingId(null);
                      showToast('success', 'Question withdrawn');
                    }}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
